/**
 * AICopilotInboxExtras — Session 5 frontend additions to InboxPage.
 *
 * Renders THREE pieces on top of the conversation detail view:
 *   1. Intent pill (with urgency + sentiment)
 *   2. Lead tier pill (cold/warm/hot/burning) — shown if customer
 *      has been scored
 *   3. AI Summary expandable panel — collapsible, with refresh
 *
 * All three are hidden when the relevant master switch is off:
 *   - lead_scoring_enabled
 *   - chat_summary_enabled
 * (The intent pill is part of the master ai_copilot enable.)
 *
 * Read-only — no auto-send, no auto-update of any inbox state. The
 * agent uses these as decision aids.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Sparkles, RefreshCw, Flame, AlertTriangle,
  Snowflake, ThermometerSun, Coffee, Loader2, FileText,
  PauseCircle, CheckCircle2,
} from 'lucide-react';
import axios from 'axios';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const TIER_STYLES = {
  burning:  { icon: Flame, color: 'bg-red-100 text-red-800 border-red-300',  label: 'Burning' },
  hot:      { icon: ThermometerSun, color: 'bg-amber-100 text-amber-800 border-amber-300', label: 'Hot' },
  warm:     { icon: Coffee, color: 'bg-blue-100 text-blue-800 border-blue-300', label: 'Warm' },
  cold:     { icon: Snowflake, color: 'bg-slate-100 text-slate-700 border-slate-300', label: 'Cold' },
};
const URGENCY_STYLES = {
  high:   'bg-red-100 text-red-800',
  medium: 'bg-amber-100 text-amber-800',
  low:    'bg-slate-100 text-slate-700',
};

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

export default function AICopilotInboxExtras({ conversation }) {
  const [enabled, setEnabled] = useState(() =>
    localStorage.getItem('ai_copilot_enabled') === 'true');
  const [enabledKnown, setEnabledKnown] = useState(false);
  const [features, setFeatures] = useState({
    lead_scoring: true, chat_summary: true,
  });
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState('');
  const [leadScore, setLeadScore] = useState(null);
  const [leadLoading, setLeadLoading] = useState(false);

  // Load settings flags.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/ai-copilot/settings`,
          { headers: authHeaders() });
        if (cancelled) return;
        const s = res.data || {};
        const flag = !!s.enabled;
        setEnabled(flag);
        localStorage.setItem('ai_copilot_enabled', String(flag));
        setFeatures({
          lead_scoring: s.lead_scoring_enabled !== false,
          chat_summary: s.chat_summary_enabled !== false,
        });
      } catch {
        setEnabled(false);
      } finally {
        if (!cancelled) setEnabledKnown(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Reset state when conversation switches.
  useEffect(() => {
    setSummary(null);
    setSummaryOpen(false);
    setSummaryError('');
    setLeadScore(null);
  }, [conversation?.id]);

  // Use the cached lead score from the conversation's customer doc.
  // (Auto-recompute happens on new inbound; we just READ the cached
  // value here so we don't burn an extra LLM call per conversation open.)
  const intent = conversation?.ai_last_intent;

  const fetchSummary = useCallback(async (force = false) => {
    if (!conversation?.id) return;
    setSummaryLoading(true);
    setSummaryError('');
    try {
      const res = await axios.post(`${API_BASE}/ai-copilot/chat-summary`,
        { conversation_id: conversation.id, force_refresh: !!force },
        { headers: authHeaders() });
      setSummary(res.data);
    } catch (e) {
      setSummaryError(e?.response?.data?.detail || 'Failed to generate summary');
    } finally {
      setSummaryLoading(false);
    }
  }, [conversation?.id]);

  const fetchLeadScore = useCallback(async () => {
    if (!conversation?.id) return;
    setLeadLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/ai-copilot/lead-score`,
        { conversation_id: conversation.id },
        { headers: authHeaders() });
      setLeadScore(res.data);
    } catch {
      // silent — non-critical
    } finally {
      setLeadLoading(false);
    }
  }, [conversation?.id]);

  if (!enabledKnown || !enabled) return null;

  const tier = leadScore?.tier || conversation?.ai_lead_tier;
  const score = leadScore?.score ?? conversation?.ai_lead_score;
  const tierMeta = tier ? TIER_STYLES[tier] : null;
  const autoStatus = conversation?.last_auto_reply_status;

  return (
    <div className="contents" data-testid="ai-copilot-inbox-extras">
      {/* `contents` lets each pill be a direct child of the parent
          toolbar's flex container — they share the wrap row instead
          of forming a sub-block that pushes other items to a new line. */}
      <>
        {intent && (
          <span
            className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium border border-wt-border max-w-[160px] truncate`}
            title={`Confidence: ${intent.confidence ?? '?'}%  ·  Sentiment: ${intent.sentiment ?? '?'}`}
            data-testid="ai-intent-pill"
          >
            <Sparkles className="h-2.5 w-2.5 text-wt-gold flex-none" />
            <span className="truncate">{intent.intent.replaceAll('_', ' ')}</span>
            <span className={`ml-0.5 px-1 rounded ${URGENCY_STYLES[intent.urgency] || URGENCY_STYLES.low}`}>
              {intent.urgency}
            </span>
            {intent.needs_human && (
              <AlertTriangle className="h-2.5 w-2.5 text-amber-600 ml-0.5 flex-none" />
            )}
          </span>
        )}

        {features.lead_scoring && tierMeta && (
          <span
            className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${tierMeta.color}`}
            title={`Lead score: ${score ?? '?'} / 100`}
            data-testid={`ai-lead-tier-${tier}`}
          >
            <tierMeta.icon className="h-2.5 w-2.5" />
            <span>{tierMeta.label}</span>
            {typeof score === 'number' && (
              <span className="ml-0.5 opacity-80">· {score}</span>
            )}
          </span>
        )}

        {features.lead_scoring && !tierMeta && (
          <Button variant="ghost" size="sm"
                  onClick={fetchLeadScore} disabled={leadLoading}
                  className="h-6 px-1.5 text-[10px] gap-1 text-wt-text-muted hover:text-wt-text-primary"
                  title="Score this lead"
                  data-testid="ai-score-lead-btn">
            {leadLoading ? <Loader2 className="h-3 w-3 animate-spin" />
                         : <Sparkles className="h-3 w-3" />}
            <span className="hidden sm:inline">Score lead</span>
          </Button>
        )}

        {/* Auto-reply outcome chip — shows the WHY when the worker
            skipped (e.g. conversation assigned, outside office hours,
            confidence too low). Quiet when there's nothing to show. */}
        {autoStatus && <AutoReplyStatusChip status={autoStatus} />}

        {/* AI Summary — Popover (NO inline expansion that pushes the chat).
            On mobile the popover content adopts a sheet-like full-width
            layout via Tailwind responsive width classes. Max-height +
            internal scroll so long summaries never cover the composer. */}
        {features.chat_summary && (
          <Popover open={summaryOpen} onOpenChange={(o) => {
            setSummaryOpen(o);
            if (o && !summary && !summaryLoading) fetchSummary(false);
          }}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm"
                      className="h-6 px-1.5 text-[10px] gap-1 text-wt-text-muted hover:text-wt-text-primary"
                      title="AI conversation summary"
                      data-testid="ai-summary-toggle-btn">
                <FileText className="h-3 w-3 text-wt-gold" />
                <span className="hidden sm:inline">AI Summary</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end" side="top" sideOffset={8}
              className="w-[min(420px,calc(100vw-1.5rem))] max-h-[60vh] overflow-y-auto p-3 space-y-2"
              data-testid="ai-summary-panel"
            >
              {summaryLoading && (
                <div className="flex items-center gap-2 text-xs text-wt-text-muted">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Generating summary…
                </div>
              )}
              {summaryError && (
                <div className="text-xs text-rose-600">{summaryError}</div>
              )}
              {summary && !summaryLoading && (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm text-wt-text-primary leading-relaxed"
                       data-testid="ai-summary-text">
                      {summary.summary}
                    </p>
                    <Button variant="ghost" size="sm"
                            onClick={() => fetchSummary(true)}
                            className="h-6 text-[10px] gap-1 flex-none text-wt-text-muted hover:text-wt-text-primary"
                            data-testid="ai-summary-refresh-btn">
                      <RefreshCw className="h-3 w-3" />
                      Refresh
                    </Button>
                  </div>
                  {summary.customer_mood && (
                    <div className="text-[11px] text-wt-text-muted">
                      Mood: <span className="font-medium text-wt-text-primary">{summary.customer_mood}</span>
                      {summary.cached && <span className="ml-2 italic">(cached)</span>}
                    </div>
                  )}
                  {summary.key_questions?.length > 0 && (
                    <div className="text-[11px]" data-testid="ai-summary-questions">
                      <div className="text-wt-text-muted font-medium mb-0.5">Open questions:</div>
                      <ul className="list-disc ml-4 text-wt-text-primary">
                        {summary.key_questions.map((q, i) => (
                          <li key={i}>{q}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {summary.unresolved_issues?.length > 0 && (
                    <div className="text-[11px]" data-testid="ai-summary-issues">
                      <div className="text-amber-700 font-medium mb-0.5">Unresolved:</div>
                      <ul className="list-disc ml-4 text-wt-text-primary">
                        {summary.unresolved_issues.map((q, i) => (
                          <li key={i}>{q}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {summary.action_items?.length > 0 && (
                    <div className="text-[11px]" data-testid="ai-summary-actions">
                      <div className="text-emerald-700 font-medium mb-0.5">Next actions:</div>
                      <ul className="list-disc ml-4 text-wt-text-primary">
                        {summary.action_items.map((q, i) => (
                          <li key={i}>{q}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </PopoverContent>
          </Popover>
        )}
      </>
    </div>
  );
}


// ───── Auto-Reply Status Chip ─────────────────────────────────────────
// Human-readable label for every skip-reason persisted by
// /app/backend/ai_copilot/auto_reply.py:_record_status. New reasons
// fall through to a humanised version of the slug.
const _SKIP_LABELS = {
  conversation_assigned: 'Assigned to a human',
  ai_auto_reply_disabled_on_thread: 'AI disabled on this thread',
  handoff_in_progress: 'Human handoff active',
  quiet_hours: 'Quiet hours',
  high_value_customer: 'High-value customer — held for human',
  inbound_too_old: 'Inbound too old (>60s) — human likely replied',
  no_candidate_reply: 'No safe reply available',
  needs_human: 'Intent needs a human',
  send_failed: 'WhatsApp send failed',
  human_interrupted_during_delay: 'Human jumped in during reply delay',
  assigned_during_delay: 'Assigned during reply delay',
  auto_sent: 'Auto-replied',
  low_confidence_fallback: 'Sent courtesy fallback reply',
};

function _formatSkipReason(reason) {
  if (!reason) return '';
  if (_SKIP_LABELS[reason]) return _SKIP_LABELS[reason];
  if (reason.startsWith('max_auto_replies_reached:')) {
    const cap = reason.split(':')[1];
    return `Auto-reply cap reached (${cap})`;
  }
  if (reason.startsWith('confidence_below_threshold:')) {
    return `Confidence too low (${reason.split(':')[1]})`;
  }
  if (reason.startsWith('outside_office_hours:')) {
    const sub = reason.split(':')[1];
    if (sub === 'holiday') return 'Outside hours — public holiday';
    if (sub === 'closed_day') return 'Outside hours — closed day';
    if (sub === 'before_open') return 'Outside hours — before opening';
    if (sub === 'after_close') return 'Outside hours — after closing';
    return 'Outside office hours';
  }
  if (reason.startsWith('mode_')) {
    return 'Intent blocked by mode';
  }
  if (reason.startsWith('reply_scrubbed:')) {
    const flag = reason.split(':')[1] || '';
    const FLAG_LABELS = {
      'replaced_with_safe_fallback': 'AI tried to invent details — blocked',
      'delivery_date_promise_without_high_confidence': 'AI promised a delivery date — blocked',
      'discount_promise': 'AI promised a discount — blocked',
      'hallucinated_order_ref': 'AI invented an order number — blocked',
      'absolute_guarantee': 'AI used an absolute guarantee — blocked',
      'unverified_stock_claim': 'AI claimed stock without data — blocked',
      'free_delivery_claim_without_policy': 'AI claimed free delivery — blocked',
      'cod_claim_without_policy': 'AI claimed COD — blocked',
      'bnpl_claim_without_policy': 'AI claimed BNPL — blocked',
      'empty_reply': 'AI returned empty reply',
    };
    if (FLAG_LABELS[flag]) return FLAG_LABELS[flag];
    if (flag.startsWith('hallucinated_order_ref')) {
      return 'AI invented an order number — blocked';
    }
    if (flag.startsWith('avoid_word:')) {
      return `AI used a banned word — blocked`;
    }
    return 'AI reply scrubbed by safety check';
  }
  return reason.replaceAll('_', ' ');
}

function _formatRelativeTime(iso) {
  if (!iso) return '';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return 'just now';
    const m = Math.round(ms / 60_000);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
  } catch {
    return '';
  }
}

function AutoReplyStatusChip({ status }) {
  const sent = status?.result === 'sent';
  const reason = status?.reason || '';
  const label = _formatSkipReason(reason);
  if (!label) return null;
  const cls = sent
    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : 'bg-slate-50 text-slate-700 border-slate-200';
  const Icon = sent ? CheckCircle2 : PauseCircle;
  const conf = status?.confidence;
  const ts = _formatRelativeTime(status?.at);
  const title = [
    sent ? 'Last AI auto-reply' : 'AI auto-reply skipped',
    `Reason: ${label}`,
    typeof conf === 'number' ? `Confidence: ${conf}%` : null,
    status?.intent ? `Intent: ${status.intent}` : null,
    ts ? `When: ${ts}` : null,
  ].filter(Boolean).join('\n');
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium border max-w-[220px] ${cls}`}
      title={title}
      data-testid={`ai-auto-reply-status-${sent ? 'sent' : 'skipped'}`}
    >
      <Icon className="h-2.5 w-2.5 flex-none" />
      <span className="truncate">{sent ? 'AI replied' : label}</span>
      {ts && <span className="ml-0.5 opacity-70">· {ts}</span>}
    </span>
  );
}
